import { inject, Injectable, signal } from '@angular/core';
import Swal from 'sweetalert2';
import { FullReservation } from '../../../models/reservation/full-reservation';
import { Reservation } from '../../../models/reservation/reservation';
import { ReservationApiService } from '../../repository/flexseat/reservation-api.service';
import { DateService } from '../util/date/date.service';
import { GraphService } from '../util/graph/graph.service';

@Injectable({
  providedIn: 'root'
})
export class ReservationService {

  
  private readonly reservationApi = inject(ReservationApiService);
  private readonly graphService = inject(GraphService);
  private readonly dateService = inject(DateService);

  private reservationRequest = signal<{date: string, secao: number, periodo: string } | null>(null);

  reservationReq = this.reservationRequest.asReadonly();

  buildingReservations = signal<FullReservation[]>([]);

  userReservations = signal<FullReservation[]>([]);
  userTodayReservation = signal<FullReservation | null>(null);
  userFutureReservation = signal<FullReservation[]>([]);
  userSelectedDateReservation = signal<FullReservation | null>(null);

  newUserReservation = signal<Reservation | null>(null);

  /** Utiliraty methods for data treatment */
  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private formatDateTime(date: Date): string {
    return date.toISOString();
  }

  setReservationData (data: string, secao: number, periodo: string = '3') {
    this.reservationRequest.set({
      date: data,
      secao: secao,
      periodo: periodo
    });
  }

  /**
   * Checks if the desk is available, is busy or if its reserved by the logged user 
   * and the kind of color it returns
   */

  deskStatusCheck(desk: string, user: string) {

    const deskOne = this.buildingReservations().find((item) => item.tableName === desk);

    if(deskOne?.tableStatus == 'BLOCKED') {
      return 'busy';
    }

    if(deskOne?.username === user) {
      return 'myplace';
    }

    if(deskOne?.username) {
      return 'busy';
    }

    return 'available';

  }


  /**
   * Fetch user reservations from the API and update the state.
   */
  fetchUserReservations(user: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.reservationApi.getUserReservations(user).subscribe({
        next: (reservations) => {
          if (reservations) {

            this.userReservations.set(reservations);
            

            const today = this.formatDate(this.dateService.getCurrentDate());

            this.userTodayReservation.set(reservations.find(r => {
              const reservationDate = r.reservationDate ? this.formatDate(new Date(r.reservationDate)) : null;
              return reservationDate === today;
            }) || null);

            this.userFutureReservation.set(reservations.filter(r => {
              const reservationDate = r.reservationDate ? this.formatDate(new Date(r.reservationDate)) : null;
              return reservationDate && reservationDate >= today;
            }));

            resolve();
          }
          resolve();
        },
        error: (error) => {
          console.error('Error fetching reservations:', error);
          this.userReservations.set([]);
          reject(error);
        }
      });
    });
  }

    /** Fetch reservations by building from the API and update the state.*/
  fetchBuildingReservations( reqData: { date: string; secao: number; periodo: string; } | null) : Promise<void> {
    return new Promise((resolve, reject) => {

      this.userSelectedDateReservation.set(this.userReservations().find(r => {
            const reservationDate = r.reservationDate ? this.formatDate(new Date(r.reservationDate)) : null;
            return reservationDate === reqData!.date;
          }) || null);
      
      this.reservationApi.getFullReservations(reqData!.date, reqData!.secao, reqData!.periodo).subscribe({
        next: (reservations) => {

          this.buildingReservations.set(reservations);

          resolve();
        },
        error: (error) => {
          console.error('❌ Erro na requisição:',  error);
          reject(error);
        }
      });
    });
  }
/** Method for cancelling an existing an user reservation  */
  cancelReservationMethod(reserva: FullReservation, responsible: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.reservationApi.cancelReservation(reserva, responsible).subscribe({
        next: () => {
          this.fetchUserReservations(reserva.username!);
          resolve();
        },
        error: (error) => {
          console.error('Error canceling reservation:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Method for confirming check-in reservation
   */

  checkInReservationMethod(reserva: FullReservation): Promise<void> {
    return new Promise((resolve, reject) => {

      this.reservationApi.checkInReservation(reserva).subscribe({

        next: () => {
          this.fetchUserReservations(reserva.username!);
          resolve();
        },
        error: (error) => {
          console.error('Error during check-in:', error);
          reject(error);
        }

      });
    });
  }

  /**
   * Handles the process of scheduling or updating a reservation for a given user and table ID.
   * If the user already has a reservation on the selected date, updates the reservation
   * If user has no reservation, prompt for new reservation   */

  scheduleReservation(id: number, user: string) {

    if (this.userSelectedDateReservation()) {

      Swal.fire({
        title: 'Você já possui uma reserva para esta data',
        text: 'Deseja atualizar para a mesa selecionada?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não'
      }).then((result) => {
        if (result.isConfirmed) {
          this.generateUpdateReservationData(id);
          this.reservationApi.updateReservationTable(this.newUserReservation()!).subscribe({
            next: () => {
              Swal.fire({ icon: 'success', title: 'Reserva atualizada com sucesso!' });
              this.refreshReservations();
              this.fetchUserReservations(this.graphService.userGraphData()!.employeeId);
            },
            error: (error) => {
              console.error('Erro ao atualizar reserva:', error);
              Swal.fire({ icon: 'error', title: 'Erro ao atualizar reserva', text: 'Tente novamente.' });
            }
          });
        }
      });

    } else {

      Swal.fire({
        title: 'Deseja realizar uma nova reserva?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não'
      }).then((result) => {
        if (result.isConfirmed) {
          this.generateNewReservationData(id, user);
          this.reservationApi.createReservation(this.newUserReservation()!).subscribe({
            next: () => {
              Swal.fire({ icon: 'success', title: 'Reserva realizada com sucesso!' });
              this.refreshReservations();
              this.fetchUserReservations(this.graphService.userGraphData()!.employeeId);
            },
            error: (error) => {
              console.error('Erro ao criar reserva:', error);
              
              let errorMessage = '';
              if (typeof error.error === 'string') {
                errorMessage = error.error.trim();
              } else if (error?.error?.message) {
                errorMessage = error.error.message;
              } else if (error?.statusText) {
                errorMessage = error.statusText;
              }
              
              console.log('Mensagem de erro processada:', errorMessage);
              
              if (errorMessage === 'Data inválida para reserva.') {
                const reservationDate = this.reservationRequest()?.date;
                if (reservationDate) {
                  const [year, month, day] = reservationDate.split('-');
                  const formattedDate = `${day}/${month}/${year}`;
                  Swal.fire({ 
                    icon: 'error', 
                    title: 'Erro ao criar reserva', 
                    text: `Não foi possível efetivar a reserva para ${formattedDate}. As novas solicitações para esta data abrem hoje às 8h.` 
                  });
                } else {
                  Swal.fire({ icon: 'error', title: 'Erro ao criar reserva', text: 'Data inválida para reserva.' });
                }
              } else {
                Swal.fire({ icon: 'error', title: 'Erro ao criar reserva', text: 'Tente novamente.' });
              }
            }
          });
        }
      });

    }
  }
  /** 
   * Generates object for a new reservation
  */
  private generateNewReservationData(id:number, user: string) {
    const deskOne = this.buildingReservations().find((item) => item.tableId === id);
    this.newUserReservation.set({
      id: deskOne!.reservationId || 0,
      table_id: id,
      date: this.reservationReq()!.date,
      username: user,
      period: this.reservationReq()?.periodo || '3'
    });
  }

  /**
   * Updates object for a existing reservation
   */

  private generateUpdateReservationData(id:number) {
    
    const actualReservation = this.userSelectedDateReservation(); 
    this.newUserReservation.set({
      id: actualReservation!.reservationId!,
      table_id: id,
      date: actualReservation!.reservationDate!,
      username: actualReservation!.username!,
      period: this.reservationReq()?.periodo || '3'
    });
  }

    /** Refreshes reservations of the respective building. */
  refreshReservations() {
    const reqData = this.reservationReq();
    const date = reqData!.date;

    this.reservationApi.getFullReservations(date, reqData!.secao, reqData!.periodo).subscribe({
      next: (reservations) => {
        this.buildingReservations.set(reservations);
      },
      error: (error) => {
        console.error('Error refreshing reservations:', error);
      }
    });
  }

}
